import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import type { NoticeDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { fmtDate, fmtDateTime } from "../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  EmptyState,
  ErrorBanner,
  SuccessBanner,
  Banner,
} from "../components/ui";

const MEMBER_STATUS_LABELS: Record<string, string> = {
  active: "有効",
  payment_failed: "決済失敗",
  suspended: "停止",
  cancelled: "解約",
  pending_activation: "仮登録",
};
const MEMBER_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  active: "success",
  payment_failed: "danger",
  suspended: "warning",
  cancelled: "neutral",
  pending_activation: "warning",
};
const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  trialing: "体験中",
  active: "有効",
  past_due: "支払遅延",
  cancelled: "解約",
  expired: "期限切れ",
};
const APPLICATION_STATUS_LABELS: Record<string, string> = {
  pending_payment: "決済待ち",
  applied: "申込中",
  cancelled: "キャンセル",
  checked_in: "受付済み",
  no_show: "不参加",
};
const APPLICATION_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  pending_payment: "warning",
  applied: "neutral",
  cancelled: "neutral",
  checked_in: "success",
  no_show: "danger",
};

interface AppRow {
  id: string;
  eventId: string;
  applicationStatus: string;
  participationMode: string;
  appliedAt: string;
  eventTitle: string;
  eventStartAt: string | null;
  thumbnailKey?: string | null;
  withSocial?: boolean;
  canCancel?: boolean;
  cancelledAt?: string | null;
  cancelDeadlineAt?: string | null;
}

// 会員が選んだ参加方法のラベル
const PART_MODE_LABEL: Record<string, string> = {
  offline: "来場",
  online: "オンライン",
  hybrid: "来場", // 旧データ互換 (本来は申込時に offline/online に確定)
};

// F-2-a Q3: 参加予定の判定基準
const UPCOMING_APP_STATUSES = new Set([
  "pending_payment",
  "applied",
  "checked_in",
]);

function initialOf(name: string): string {
  // 表示名の先頭 1 文字 (非空) / 無ければ '?'
  const s = (name ?? "").trim();
  if (s.length === 0) return "?";
  // surrogate pair 考慮 (絵文字が名前先頭でも崩れないように)
  return [...s][0] ?? "?";
}

interface LineStatus {
  enabled: boolean;
  connected: boolean;
  displayName?: string | null;
  linkedAt?: string;
  pendingCode?: { code: string; expiresAt: string } | null;
}
interface LineInfo {
  enabled: boolean;
  friendAddUrl: string;
}

export function MyPage() {
  const { me, refresh } = useAuth();
  const [apps, setApps] = useState<AppRow[]>([]);
  const [notices, setNotices] = useState<NoticeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // フリガナ編集
  const [editingKana, setEditingKana] = useState(false);
  const [kanaDraft, setKanaDraft] = useState("");
  const [kanaBusy, setKanaBusy] = useState(false);
  const [kanaErr, setKanaErr] = useState<string | null>(null);
  const [kanaMsg, setKanaMsg] = useState<string | null>(null);
  // LINE 連携
  const [lineStatus, setLineStatus] = useState<LineStatus | null>(null);
  const [lineInfo, setLineInfo] = useState<LineInfo | null>(null);
  const [lineBusy, setLineBusy] = useState(false);
  const [lineMsg, setLineMsg] = useState<string | null>(null);
  const [lineErr, setLineErr] = useState<string | null>(null);
  const [lineConfirmUnlink, setLineConfirmUnlink] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get<{ items: AppRow[] }>("/events/me/applications"),
      api.get<{ items: NoticeDTO[] }>("/notices?perPage=50"),
    ])
      .then((results) => {
        if (cancelled) return;
        const [appsRes, noticesRes] = results;
        if (appsRes.status === "fulfilled") setApps(appsRes.value.items);
        if (noticesRes.status === "fulfilled")
          setNotices(noticesRes.value.items);
        const anyErr = results.find((r) => r.status === "rejected");
        if (anyErr && anyErr.status === "rejected") {
          setErr(
            (anyErr.reason as Error).message ?? "情報の取得に失敗しました",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // LINE 連携 status + info
  const refreshLine = async () => {
    try {
      const [s, i] = await Promise.all([
        api.get<LineStatus>("/member/line/status"),
        api.get<LineInfo>("/member/line/info"),
      ]);
      setLineStatus(s);
      setLineInfo(i);
    } catch {
      // LINE 機能無効時は 503 等で落ちるが、画面の主機能に影響させない
      setLineStatus({ enabled: false, connected: false });
      setLineInfo({ enabled: false, friendAddUrl: "" });
    }
  };
  useEffect(() => {
    void refreshLine();
  }, []);

  const issueLineCode = async () => {
    setLineBusy(true);
    setLineErr(null);
    setLineMsg(null);
    try {
      await api.post("/member/line/code");
      await refreshLine();
      setLineMsg("連携コードを発行しました。LINE でコードを送信してください。");
    } catch (e) {
      setLineErr((e as Error).message ?? "コード発行に失敗しました");
    } finally {
      setLineBusy(false);
    }
  };
  const unlinkLine = async () => {
    setLineBusy(true);
    setLineErr(null);
    setLineMsg(null);
    try {
      await api.delete("/member/line/link");
      setLineConfirmUnlink(false);
      await refreshLine();
      setLineMsg("LINE 連携を解除しました。");
    } catch (e) {
      setLineErr((e as Error).message ?? "連携解除に失敗しました");
    } finally {
      setLineBusy(false);
    }
  };

  const startEditKana = () => {
    setKanaDraft(me?.member?.kana ?? "");
    setKanaErr(null);
    setKanaMsg(null);
    setEditingKana(true);
  };
  const saveKana = async () => {
    setKanaBusy(true);
    setKanaErr(null);
    setKanaMsg(null);
    try {
      await api.patch("/member/me", { kana: kanaDraft.trim() });
      await refresh();
      setEditingKana(false);
      setKanaMsg("フリガナを更新しました");
    } catch (e) {
      setKanaErr((e as Error).message ?? "更新に失敗しました");
    } finally {
      setKanaBusy(false);
    }
  };

  // 参加予定 (未来 × active 系) / 参加履歴 (それ以外)
  const { upcomingApps, pastApps } = useMemo(() => {
    const now = Date.now();
    const upcoming: AppRow[] = [];
    const past: AppRow[] = [];
    for (const a of apps) {
      const isUpcoming =
        UPCOMING_APP_STATUSES.has(a.applicationStatus) &&
        a.eventStartAt &&
        new Date(a.eventStartAt).getTime() > now;
      if (isUpcoming) upcoming.push(a);
      else past.push(a);
    }
    upcoming.sort((x, y) => {
      const xt = x.eventStartAt ? new Date(x.eventStartAt).getTime() : 0;
      const yt = y.eventStartAt ? new Date(y.eventStartAt).getTime() : 0;
      return xt - yt;
    });
    past.sort((x, y) => {
      const xt = x.eventStartAt ? new Date(x.eventStartAt).getTime() : 0;
      const yt = y.eventStartAt ? new Date(y.eventStartAt).getTime() : 0;
      return yt - xt; // 新しい順
    });
    return { upcomingApps: upcoming, pastApps: past };
  }, [apps]);

  const unreadNotices = useMemo(
    () => notices.filter((n) => n.isRead === false),
    [notices],
  );
  const unreadTop3 = unreadNotices.slice(0, 3);

  const member = me?.member ?? null;
  const displayName = member?.displayName ?? me?.user.email ?? "ゲスト";
  const memberNo = member?.memberNo ?? "-";
  const memberStatus = member?.status;
  const memberStatusTone = memberStatus
    ? MEMBER_STATUS_TONE[memberStatus] ?? "neutral"
    : "neutral";
  const memberStatusLabel = memberStatus
    ? MEMBER_STATUS_LABELS[memberStatus] ?? memberStatus
    : "-";

  return (
    <div className="space-y-6">
      {/* F-2-A: プロフィールカード (アバター + 名前 + 会員番号 + ステータス) */}
      <section className="card p-5 md:p-7">
        <div className="flex items-center gap-4 md:gap-5">
          <div
            aria-hidden="true"
            className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-tora text-tora-accent flex items-center justify-center text-2xl md:text-3xl font-bold shrink-0 shadow-sm"
          >
            {initialOf(displayName)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-tora-ink/60 tracking-widest">
              MY PAGE
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-tora truncate">
              {displayName}
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-tora-ink/70">
                会員番号 {memberNo}
              </span>
              <StatusBadge tone={memberStatusTone}>
                {memberStatusLabel}
              </StatusBadge>
            </div>
          </div>
        </div>
      </section>

      {err && <ErrorBanner>{err}</ErrorBanner>}

      {/* F-R-c: 受付用 QR (淡いピンク tint / 画像準拠) */}
      <section className="rounded-xl border border-tora/20 shadow-sm p-6 md:p-8 text-center bg-red-50/60">
        <p className="text-xs md:text-sm text-tora-ink/70">
          イベント受付で提示
        </p>
        <h3 className="text-xl md:text-2xl font-bold mt-1 text-tora-ink">
          受付用 QR コード
        </h3>
        <Link
          to="/mypage/qr"
          className="btn-primary btn-lg w-full md:w-auto md:px-10 mt-4 font-bold shadow"
        >
          QR を表示する 🎫
        </Link>
        <p className="text-xs text-tora-ink/60 mt-3">
          署名・有効期限つきで安全に表示されます
        </p>
      </section>

      {/* F-2-C/D/E/F: 2:1 grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* 左カラム: 参加予定 + 参加履歴 */}
        <div className="md:col-span-2 space-y-6">
          {/* 参加予定 (新設) */}
          <section className="card p-5 md:p-6">
            <SectionHeading
              level={3}
              action={
                <Link
                  to="/events"
                  className="text-sm text-tora hover:underline"
                >
                  イベント一覧 →
                </Link>
              }
            >
              参加予定
            </SectionHeading>
            {loading ? (
              <Loading />
            ) : upcomingApps.length === 0 ? (
              <EmptyState
                icon="🗓"
                title="参加予定のイベントはありません"
                description={
                  <Link
                    to="/events"
                    className="text-tora underline text-xs"
                  >
                    募集中のイベントを見る
                  </Link>
                }
              />
            ) : (
              <ul className="space-y-2">
                {upcomingApps.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/events/${a.eventId}`}
                      className="flex items-center gap-3 rounded p-2 -m-2 hover:bg-black/[0.03] transition-colors"
                    >
                      {uploadsUrl(a.thumbnailKey) ? (
                        <img
                          src={uploadsUrl(a.thumbnailKey)!}
                          alt=""
                          className="w-16 h-16 md:w-20 md:h-20 object-cover rounded border border-black/10 shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded border border-black/10 bg-black/5 flex items-center justify-center text-2xl shrink-0">
                          🗓
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{a.eventTitle}</p>
                        {a.eventStartAt && (
                          <p className="text-xs text-tora-ink/60 mt-0.5">
                            {fmtDateTime(a.eventStartAt)}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <StatusBadge
                            tone={
                              APPLICATION_STATUS_TONE[a.applicationStatus] ??
                              "neutral"
                            }
                            bold={a.applicationStatus === "checked_in"}
                          >
                            {APPLICATION_STATUS_LABELS[a.applicationStatus] ??
                              a.applicationStatus}
                          </StatusBadge>
                          <StatusBadge tone="neutral">
                            {PART_MODE_LABEL[a.participationMode] ?? a.participationMode}
                          </StatusBadge>
                          {a.withSocial && (
                            <StatusBadge tone="accent">懇親会</StatusBadge>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 参加履歴 (過去 / cancelled) */}
          <section className="card p-5 md:p-6">
            <SectionHeading level={3}>参加履歴</SectionHeading>
            {loading ? (
              <Loading />
            ) : pastApps.length === 0 ? (
              <EmptyState title="過去の参加履歴はまだありません" />
            ) : (
              <ul className="divide-y divide-black/5">
                {pastApps.map((a) => {
                  const tone =
                    APPLICATION_STATUS_TONE[a.applicationStatus] ?? "neutral";
                  const isCancelled = a.applicationStatus === "cancelled";
                  return (
                    <li
                      key={a.id}
                      className="py-2 text-sm flex items-center gap-2"
                    >
                      <StatusBadge
                        tone={tone}
                        bold={a.applicationStatus === "checked_in"}
                        className={isCancelled ? "line-through opacity-70" : ""}
                      >
                        {APPLICATION_STATUS_LABELS[a.applicationStatus] ??
                          a.applicationStatus}
                      </StatusBadge>
                      {!isCancelled && (
                        <StatusBadge tone="neutral">
                          {PART_MODE_LABEL[a.participationMode] ?? a.participationMode}
                        </StatusBadge>
                      )}
                      {a.withSocial && !isCancelled && (
                        <StatusBadge tone="accent">懇親会</StatusBadge>
                      )}
                      <Link
                        to={`/events/${a.eventId}`}
                        className={`hover:underline truncate ${
                          isCancelled ? "line-through text-tora-ink/50" : ""
                        }`}
                      >
                        {a.eventTitle}
                      </Link>
                      {a.eventStartAt && (
                        <span className="ml-auto text-xs text-tora-ink/60 shrink-0">
                          {fmtDate(a.eventStartAt)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* 右カラム: 未読お知らせ (条件) + 会員情報 */}
        <div className="md:col-span-1 space-y-6">
          {/* 未読お知らせ (件数 + 最新 3 件タイトル) */}
          {!loading && unreadNotices.length > 0 && (
            <section className="card p-5 md:p-6">
              <SectionHeading
                level={3}
                action={
                  <Link
                    to="/notices"
                    className="text-sm text-tora hover:underline"
                  >
                    一覧 →
                  </Link>
                }
              >
                未読のお知らせ
              </SectionHeading>
              <p className="flex items-baseline gap-1 mb-3">
                <span className="text-3xl font-bold text-tora">
                  {unreadNotices.length}
                </span>
                <span className="text-sm text-tora-ink/70">件 未読</span>
              </p>
              <ul className="divide-y divide-black/5 -mx-2">
                {unreadTop3.map((n) => (
                  <li key={n.id}>
                    <Link
                      to={`/notices/${n.id}`}
                      className="block px-2 py-2 rounded hover:bg-black/[0.03] transition-colors"
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <StatusBadge tone="accent" bold>
                          未読
                        </StatusBadge>
                        {n.publishedAt && (
                          <span className="text-xs text-tora-ink/60">
                            {fmtDate(n.publishedAt)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium leading-snug">
                        {n.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 会員情報 */}
          <section className="card p-5 md:p-6">
            <SectionHeading level={3}>会員情報</SectionHeading>
            <dl className="grid grid-cols-[7rem_1fr] gap-y-2 text-sm">
              <dt className="text-tora-ink/60">表示名</dt>
              <dd className="font-medium">{member?.displayName ?? "-"}</dd>
              <dt className="text-tora-ink/60">フリガナ</dt>
              <dd>
                {editingKana ? (
                  <div className="space-y-2">
                    <input
                      className="input"
                      value={kanaDraft}
                      onChange={(e) => setKanaDraft(e.target.value)}
                      placeholder="レイワ タロウ"
                      autoFocus
                    />
                    <div className="flex items-center gap-2">
                      <button
                        className="btn-primary text-xs px-3 py-1"
                        onClick={saveKana}
                        disabled={kanaBusy}
                      >
                        {kanaBusy ? "保存中…" : "保存"}
                      </button>
                      <button
                        className="btn-outline text-xs px-3 py-1"
                        onClick={() => setEditingKana(false)}
                        disabled={kanaBusy}
                      >
                        キャンセル
                      </button>
                    </div>
                    {kanaErr && <ErrorBanner>{kanaErr}</ErrorBanner>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {member?.kana ? member.kana : "未設定"}
                    </span>
                    <button
                      className="text-xs text-tora underline"
                      onClick={startEditKana}
                    >
                      編集
                    </button>
                  </div>
                )}
                {kanaMsg && !editingKana && (
                  <p className="text-xs text-green-600 mt-1">{kanaMsg}</p>
                )}
              </dd>
              <dt className="text-tora-ink/60">会員番号</dt>
              <dd className="font-mono">{member?.memberNo ?? "-"}</dd>
              <dt className="text-tora-ink/60">ステータス</dt>
              <dd>
                <StatusBadge tone={memberStatusTone}>
                  {memberStatusLabel}
                </StatusBadge>
              </dd>
              <dt className="text-tora-ink/60">サブスク</dt>
              <dd>
                {member?.subscription?.status
                  ? SUBSCRIPTION_STATUS_LABELS[member.subscription.status] ??
                    member.subscription.status
                  : "未契約"}
              </dd>
              {member?.subscription?.currentPeriodEnd && (
                <>
                  <dt className="text-tora-ink/60">次回更新</dt>
                  <dd className="text-tora-ink/80">
                    {fmtDate(member.subscription.currentPeriodEnd)}
                  </dd>
                </>
              )}
            </dl>
          </section>
        </div>
      </div>

      {/* LINE 連携 (Phase 1) */}
      {lineStatus?.enabled && (
        <section className="card p-5 md:p-6">
          <SectionHeading
            level={3}
            subtitle="公式アカウントを友だち追加して、イベント情報などを LINE で受け取れます。"
          >
            LINE 連携
          </SectionHeading>

          {lineErr && <ErrorBanner className="mb-3">{lineErr}</ErrorBanner>}
          {lineMsg && <SuccessBanner className="mb-3">{lineMsg}</SuccessBanner>}

          {lineStatus.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <StatusBadge tone="success" bold>
                  ✅ 連携済み
                </StatusBadge>
                {lineStatus.displayName && (
                  <span className="text-sm text-tora-ink/70">
                    {lineStatus.displayName} さん
                  </span>
                )}
                {lineStatus.linkedAt && (
                  <span className="text-xs text-tora-ink/50 ml-auto">
                    {fmtDate(lineStatus.linkedAt)} 連携
                  </span>
                )}
              </div>
              {lineConfirmUnlink ? (
                <Banner tone="warning">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm">
                      LINE 連携を解除します。今後イベント通知などは届かなくなります。よろしいですか?
                    </span>
                    <button
                      className="btn-primary text-xs"
                      onClick={unlinkLine}
                      disabled={lineBusy}
                    >
                      {lineBusy ? "処理中…" : "解除する"}
                    </button>
                    <button
                      className="btn-outline text-xs"
                      onClick={() => setLineConfirmUnlink(false)}
                      disabled={lineBusy}
                    >
                      キャンセル
                    </button>
                  </div>
                </Banner>
              ) : (
                <button
                  className="btn-outline text-sm"
                  onClick={() => setLineConfirmUnlink(true)}
                  disabled={lineBusy}
                >
                  連携を解除する
                </button>
              )}
            </div>
          ) : lineStatus.pendingCode ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-tora/5 border border-tora/20 p-5 text-center">
                <p className="text-[11px] text-tora tracking-widest font-semibold">
                  連携コード
                </p>
                <p className="mt-2 text-2xl md:text-3xl font-bold font-mono tracking-[0.2em] text-tora-ink">
                  {lineStatus.pendingCode.code}
                </p>
                <p className="text-xs text-tora-ink/60 mt-2">
                  有効期限: {fmtDateTime(lineStatus.pendingCode.expiresAt)}
                </p>
              </div>
              <ol className="text-sm text-tora-ink/80 space-y-1 list-decimal pl-5">
                <li>
                  LINE で公式アカウント「令和の虎 経営塾」を友だち追加
                  {lineInfo?.friendAddUrl ? (
                    <>
                      {" "}
                      (
                      <a
                        href={lineInfo.friendAddUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-tora underline"
                      >
                        友だち追加はこちら
                      </a>
                      )
                    </>
                  ) : (
                    <span className="text-tora-ink/50">
                      {" "}
                      (友だち追加リンクは準備中)
                    </span>
                  )}
                </li>
                <li>
                  上のコード <strong>{lineStatus.pendingCode.code}</strong>{" "}
                  をチャットで送信
                </li>
                <li>「連携完了」のメッセージが届けば完了</li>
              </ol>
              <button
                className="btn-outline text-sm"
                onClick={issueLineCode}
                disabled={lineBusy}
              >
                {lineBusy ? "処理中…" : "コードを再発行"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-tora-ink/70">
                LINE で連携すると、新しいイベント情報などを受け取れます。
              </p>
              <button
                className="btn-primary text-sm"
                onClick={issueLineCode}
                disabled={lineBusy || member?.status !== "active"}
              >
                {lineBusy ? "発行中…" : "LINE と連携する"}
              </button>
              {member?.status !== "active" && (
                <p className="text-xs text-tora-ink/50">
                  現在のステータスでは連携できません。
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {/* お問い合わせ */}
      <section className="card p-5 md:p-6">
        <p className="text-sm text-tora-ink/70">
          ご不明な点やお困りのことがございましたら、下記までお問い合わせください。
        </p>
        <a
          href="mailto:info@reitorakeiei.com"
          className="inline-block mt-2 text-sm text-tora font-semibold hover:underline"
        >
          info@reitorakeiei.com
        </a>
      </section>
    </div>
  );
}
